import { AI } from '@/resources/assets/images';
import { type CSSProperties, useEffect, useState } from 'react';
import { SearchType } from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';

import { useMapboxKeyQuery } from '@/hooks/useMapBoxKeyQuery';

import { Dialog } from '@/components/atoms/Dialog';
import Button from '@/components/Button/newButtons';
import { Textarea } from '@/components/atoms/Textarea';
import { ErrorMessage } from '@/components/atoms/ErrorMessage';

import { useBorderRotation } from '@/modules/sequencesRedesign/components/AiModal/hooks/useBorderRotation';
import type { DashboardGetBusinessesSuccessProps } from '@/modules/dashboard/dashboardActions';
import type {
	GetAiSearchProps,
	HttpGetAiSearchResponse
} from '@/services/aiSearchService';
import { useAnimatedEllipses } from '@/hooks/useAnimatedEllipses';
import { useAiSearchFormMutation } from './src/hooks/useFormMutation';
import { useBusinessDataMutation } from './src/hooks/useBusinessDataMutation';
import { useAiSearchFormValidation } from './src/hooks/useFormValidation';
import { FilterComponents } from '../../molecules/FilterComponents';
import { useFiltersContext } from '../../../providers/FilterProvider';
import { parseLocationToPlace } from '../../../utils/parseCoordinatesToPlace';
import type { FilterProps } from '../../../@types';

type FilterAiSearchProps = FilterProps & {
	hasFiltersApplied?: boolean;
};

export const FilterAiSearch = ({
	hasFiltersApplied,
	appliedFilters,
	onFilterChange
}: FilterAiSearchProps) => {
	const { data: mapboxKeyData } = useMapboxKeyQuery();

	const mapboxAPIKey = mapboxKeyData?.data?.data?.mapBoxAPIKey;

	const [isClearFilterAlertModalOpen, setIsClearFilterAlertModalOpen] =
		useState(false);
	const [didAiReturnEmptyFilter, setDidAiReturnEmptyFilter] = useState(false);

	const { handleChangeLocation, handleClearLocation } = useFiltersContext();
	const { setBusiness } = useBusinessDataMutation();
	const { register, handleSubmit, formState, getValues } =
		useAiSearchFormValidation();
	const { mutate, isLoading } = useAiSearchFormMutation({
		onSuccess: async ({ data }, variables) => {
			if (!data?.data?.filters) {
				setDidAiReturnEmptyFilter(true);

				return null;
			}

			const { count, cursor, businesses, filters } = data.data;

			handleSetBusinessData({
				count,
				cursor,
				result: businesses
			});

			await handleSetFilterAppliedOnAiSearch(filters);

			sendAnalytics({
				type: 'track',
				eventName: 'Filters applied by AI search',
				properties: {
					query: variables.filters,
					appliedFilters: data.data.filters
				}
			});
		}
	});

	const boxRef = useBorderRotation(isLoading, 80);
	const ellipsis = useAnimatedEllipses(isLoading);

	const handleSetBusinessData = (
		data: DashboardGetBusinessesSuccessProps['data']
	) => {
		setBusiness({
			data
		});
	};

	const handleSetFilterAppliedOnAiSearch = async (
		filters: HttpGetAiSearchResponse['filters']
	) => {
		const {
			searches = [],
			coordinates = undefined,
			state = undefined,
			country = undefined,
			includes = [],
			excludes = []
		} = filters;

		const locationName = await parseLocationToPlace({
			coordinates,
			mapboxAPIKey,
			state: state || undefined,
			country: country || undefined
		});

		const parsedIncludes = includes.map((value, index) => ({
			type: SearchType.INCLUDES,
			value,
			index: searches.length + index
		}));
		const parsedExcludes = excludes.map((value, index) => ({
			type: SearchType.EXCLUDES,
			value,
			index: (parsedIncludes.at(-1)?.index ?? 0) + index
		}));

		const mergedSearches = Array.from(
			new Set([...searches, ...parsedIncludes, ...parsedExcludes])
		);

		const stringCoordinates = Array.isArray(coordinates)
			? `[${coordinates[0]}, ${coordinates[1]}]`
			: coordinates;

		onFilterChange({
			...filters,
			coordinates: stringCoordinates,
			searches: mergedSearches
		});

		if (locationName) {
			handleChangeLocation(locationName);
		} else {
			handleClearLocation();
		}
	};

	const handleOpenAlertFilters = () => {
		setIsClearFilterAlertModalOpen(true);
	};

	const handleCancelNewFilters = () => {
		setIsClearFilterAlertModalOpen(false);
	};

	const handleApplyNewFilters = () => {
		setIsClearFilterAlertModalOpen(false);
		const filters = getValues('filters');

		mutate({ filters });
	};

	const handleClearSearchFilters = () => {
		sendAnalytics({
			type: 'track',
			eventName: 'User cleared all search filters',
			properties: {
				appliedFilters,
				querySearch: appliedFilters?.searches
			}
		});

		onFilterChange({
			...appliedFilters,
			searches: undefined,
			includes: undefined,
			excludes: undefined
		});
	};

	const onSubmit = ({ filters }: GetAiSearchProps) => {
		if (isLoading) return null;

		setDidAiReturnEmptyFilter(false);

		if (hasFiltersApplied) {
			return handleOpenAlertFilters();
		}

		mutate({ filters });
	};

	useEffect(() => {
		let timeout: NodeJS.Timeout;

		if (didAiReturnEmptyFilter) {
			timeout = setTimeout(() => {
				setDidAiReturnEmptyFilter(false);
			}, 5 * 1000);
		}

		return () => {
			clearTimeout(timeout);
		};
	}, [didAiReturnEmptyFilter]);

	return (
		<FilterComponents.Wrapper moduleName="ai-search">
			<FilterComponents.Header
				icon={AI}
				title="AI Search"
				onClearFilters={handleClearSearchFilters}
			/>

			<FilterComponents.Content>
				<p>Who are you trying to reach?</p>

				{didAiReturnEmptyFilter && <EmptyFiltersMessage />}

				<section className="flex flex-col items-center justify-center gap-4">
					<form
						onSubmit={handleSubmit(onSubmit)}
						className="flex w-full flex-col gap-3"
					>
						<div
							ref={boxRef}
							data-is-loading={isLoading}
							className="flex overflow-hidden rounded-md border border-dm-gray data-[is-loading='true']:border-[0.15625rem] data-[is-loading='true']:border-[#0000] data-[is-loading='true']:[background:padding-box_var(--bg-color),border-box_var(--border-color)]"
							style={
								{
									'--angle': '0deg',
									'--border-color':
										'linear-gradient(var(--angle), #BC82F3, #F5B9EA, #8D99FF, #AA6EEE, #FF6778, #FFBA71, #C686FF)',
									'--bg-color':
										'linear-gradient(#212121, #212121)'
								} as CSSProperties
							}
						>
							<Textarea
								size="lg"
								className="rounded-none border-none p-2 text-sm"
								placeholder="I'm a marketing agency offering social media services, looking for small coffee shops in NYC that don’t have online presence"
								{...register('filters')}
							/>
						</div>

						<ErrorMessage
							message={formState.errors.filters?.message}
						/>

						{isLoading ? (
							<span className="flex h-8 w-full cursor-not-allowed select-none items-center rounded-lg border-2 border-dm-blue pl-[4.6875rem] text-sm font-semibold">
								Searching{ellipsis}
							</span>
						) : (
							<Button
								type="submit"
								smart
								leftIcon
								fullWidth
								disabled={isLoading}
							>
								AI search
							</Button>
						)}
					</form>
				</section>
			</FilterComponents.Content>

			<ClearFilterAlertModal
				isOpen={isClearFilterAlertModalOpen}
				onCloseModal={handleCancelNewFilters}
				onApplyNewFilters={handleApplyNewFilters}
			/>
		</FilterComponents.Wrapper>
	);
};

const EmptyFiltersMessage = () => (
	<div className="flex flex-col gap-2 p-2">
		<span className="w-fit rounded-full border-2 border-dm-orange2 px-2 py-1 font-barlow font-semibold text-dm-orange2">
			No matching filters
		</span>

		<p className="font-barlow text-sm text-dm-strong-mid-grey">
			Sorry, we couldn&apos;t find results for that. Try rephrasing the
			request.
		</p>
	</div>
);

type ClearFilterAlertModalProps = {
	isOpen: boolean;
	onCloseModal: VoidFunction;
	onApplyNewFilters: VoidFunction;
};

const ClearFilterAlertModal = ({
	isOpen,
	onCloseModal,
	onApplyNewFilters
}: ClearFilterAlertModalProps) => (
	<Dialog.Root open={isOpen} onOpenChange={onCloseModal}>
		<Dialog.Content className="gap-8 border-none">
			<Dialog.Title className="flex items-center gap-2 text-xl">
				<AI className="[&>path]:fill-orange" />
				AI Search Will Reset Your Filters
			</Dialog.Title>

			<Dialog.Description>
				This will clear any filters you&apos;ve already set and start a
				new search from scratch.
			</Dialog.Description>

			<Dialog.Footer>
				<Dialog.Close asChild>
					<Button variant="text">Cancel</Button>
				</Dialog.Close>

				<Dialog.Close asChild>
					<Button variant="secondary" onClick={onApplyNewFilters}>
						Confirm
					</Button>
				</Dialog.Close>
			</Dialog.Footer>
		</Dialog.Content>
	</Dialog.Root>
);
